import React, { createContext, useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import InactivityWarning from '../components/InactivityWarning';
import { closeAllDetachableComponents } from '../utils/detachableHelpers';
import { authApi } from '../services/api';
import apiClient from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Default timeout (30 minutes)
  const [inactivityTimeout, setInactivityTimeout] = useState(30 * 60 * 1000);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  
  // Track user activity metrics. lastActivity is only read inside
  // handleUserActivity's own throttling check below and never rendered
  // anywhere, so it's a ref rather than state - as state, it changed on
  // every throttled activity tick (at most every 3s during active use),
  // which gave handleUserActivity a new identity each time. The listener
  // effect further down is keyed on [currentUser, handleUserActivity,
  // resetInactivityTimer], so that churn tore down and re-added ~10
  // window-level event listeners and re-monkey-patched axios.request
  // continuously while the user was active, instead of once per session.
  const lastActivityRef = useRef(Date.now());
  const [activityCount, setActivityCount] = useState(0);
  const [activityIntensity, setActivityIntensity] = useState('normal');
  const activityTimestampsRef = useRef([]);
  const MAX_ACTIVITY_HISTORY = 20; // Number of recent activities to track
  
  // Check authentication status - define early with useCallback
  const checkAuthStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authApi.checkAuth();
      if (response.data.authenticated) {
        setCurrentUser(response.data.user);
      }
    } catch (err) {
      localStorage.removeItem('token');
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Define logout function early to avoid reference errors
  const logout = useCallback(async (clearRemembered) => {
    // Set loading to true to prevent routing logic from executing during logout
    setLoading(true);
    
    // Immediately clear user state to prevent flashing of protected content
    setCurrentUser(null);
    
    // Clear any timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    
    // Close all detachable components
    closeAllDetachableComponents();
    
    // Remove token and local storage items
    localStorage.removeItem('token');
    // Note: keepSignedIn is NOT removed on logout - it's a persistent user preference
    localStorage.removeItem('signInExpiryTime');
    // Note: trustedDeviceToken is NOT removed on logout - it should persist for 30 days
    
    try {
      // Perform logout API call after state has been cleared
      await axios.post('/api/auth/logout');
    } catch (error) {
    }

    // Force redirect to landing page to ensure proper UI refresh
    window.location.href = '/';
  }, []);
  
  // Enhanced function to reset the inactivity timer with activity context
  const resetInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Calculate adjusted timeout based on activity patterns
    let adjustedTimeout = inactivityTimeout;
    
    // If user has been very active recently, extend the timeout slightly
    if (activityIntensity === 'high') {
      adjustedTimeout = Math.min(inactivityTimeout * 1.25, inactivityTimeout + (15 * 60 * 1000)); // Add up to 15 minutes, max 25% increase
    } 
    // If user has been minimally active, reduce the timeout slightly
    else if (activityIntensity === 'low') {
      adjustedTimeout = Math.max(inactivityTimeout * 0.75, 10 * 60 * 1000); // At least 10 minutes
    }
    
    // Set a new timer with the adjusted timeout
    inactivityTimerRef.current = setTimeout(() => {
      // Automatically log out the user when the timer expires
      logout();
    }, adjustedTimeout);

    // Let InactivityWarning know the real logout deadline just moved, so it
    // can reschedule its own warning to match (and dismiss itself if it's
    // currently showing, since real activity means there's no longer an
    // imminent timeout to warn about). A plain window event instead of
    // exposing this via context state, since `value` below is deliberately
    // memoized to exclude anything that changes this often (up to every 3s
    // during active use) - adding it there would re-render every one of the
    // ~26 components that call useAuth() on that same cadence.
    window.dispatchEvent(new CustomEvent('inactivityTimerReset'));
  }, [inactivityTimeout, logout, activityIntensity]);
  
  // Enhanced user activity handler with activity tracking
  const handleUserActivity = useCallback((event) => {
    if (!currentUser) return;
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Only count as a new activity if it's been at least 3 seconds since the last one
    // This prevents counting rapid succession of events as multiple activities.
    //
    // This handler is bound directly to window 'mousemove' (among other raw
    // events), so it used to run on every single mousemove callback - often
    // 60+ times a second while the mouse is moving, on every authenticated
    // page, for the lifetime of the session. Recording a timestamp and
    // resetInactivityTimer() (a clearTimeout+setTimeout pair) both used to
    // run on every one of those calls, unthrottled, well outside this
    // 3-second gate: constantly tearing down and recreating a timer just
    // from ordinary mouse movement. It also meant activityTimestampsRef -
    // capped at MAX_ACTIVITY_HISTORY entries - filled up with a burst of
    // near-identical timestamps within tens of milliseconds any time the
    // mouse moved, so by the time activitiesPerMinute was computed below,
    // the window it measured collapsed to a handful of milliseconds,
    // pushing activitiesPerMinute far past the 'high' threshold on every
    // calculation - permanently misclassifying ordinary mouse movement as
    // "very active" regardless of how often the user was really interacting.
    // Gating both on the same 3-second throttle fixes the performance cost
    // and gives activitiesPerMinute a real time span to measure against.
    if (timeSinceLastActivity > 3000) {
      // Record this activity timestamp
      activityTimestampsRef.current.push(now);
      // Keep only the most recent activities
      if (activityTimestampsRef.current.length > MAX_ACTIVITY_HISTORY) {
        activityTimestampsRef.current.shift();
      }

      setActivityCount(prevCount => prevCount + 1);

      // Determine activity intensity based on recent activity frequency
      const recentActivities = activityTimestampsRef.current;
      if (recentActivities.length >= 5) {
        const timeSpan = recentActivities[recentActivities.length - 1] - recentActivities[0];
        const activitiesPerMinute = (recentActivities.length / timeSpan) * 60000;

        if (activitiesPerMinute > 8) {
          setActivityIntensity('high'); // Very active user
        } else if (activitiesPerMinute < 2) {
          setActivityIntensity('low'); // Minimally active user
        } else {
          setActivityIntensity('normal'); // Normal activity level
        }
      }

      // Update last activity time
      lastActivityRef.current = now;

      // Reset the inactivity timer with the new context
      resetInactivityTimer();
    }
  }, [currentUser, resetInactivityTimer]);
  
  // Function to update inactivity timeout duration
  const updateInactivityTimeout = useCallback((minutes) => {
    const milliseconds = minutes * 60 * 1000;
    
    // Save timeout to localStorage so it persists across page refreshes
    localStorage.setItem('inactivityTimeout', milliseconds.toString());
    
    // Update state AFTER saving to localStorage
    setInactivityTimeout(milliseconds);
  }, []);
  
  // Note: Auth header interceptor is now handled in api.js to avoid conflicts
  
  // Login function - updated to handle email verification
  const login = useCallback(async (identifier, password, expires_at = null, twoFactorCode = null) => {
    try {
      setAuthLoading(true);
      setError('');
      
      const requestData = {
        identifier,
        password
      };
      
      // Add expires_at if provided
      if (expires_at) {
        requestData.expires_at = expires_at;
      }
      
      // Add 2FA code if provided
      if (twoFactorCode) {
        requestData.two_fa_code = twoFactorCode;
      }
      
      // Add trusted device token if available
      const trustedDeviceToken = localStorage.getItem('trustedDeviceToken');
      if (trustedDeviceToken) {
        requestData.device_token = trustedDeviceToken;
      }
      
      console.log('🔍 AuthContext login - request data:', { ...requestData, password: '***', two_fa_code: requestData.two_fa_code ? '***' : undefined });
      
      const response = await authApi.login(requestData);

      console.log('🔍 AuthContext login - API response:', response.data);

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        console.log('🔍 AuthContext login - 2FA required');
        // If we sent a device token but backend still requires 2FA,
        // the token is likely expired - clean it up
        if (trustedDeviceToken) {
          localStorage.removeItem('trustedDeviceToken');
        }
        
        return response.data;
      }

      if (response.data.access_token) {
        console.log('🔍 AuthContext login - login successful, setting token and user');
        localStorage.setItem('token', response.data.access_token);
        setCurrentUser(response.data.user);
        
        // Start the inactivity timer
        resetInactivityTimer();
        
        // Return the response data for the component to handle
        return response.data;
      } else {
        console.log('🔍 AuthContext login - no access token received');
        throw new Error('Login failed: No access token received');
      }
    } catch (err) {
      console.error('🔍 AuthContext login - error:', err);
      // Handle email verification required error
      if (err.emailVerificationRequired) {
        const emailVerificationError = {
          ...err,
          emailVerificationRequired: true,
          email: err.email,
          canResendVerification: err.canResendVerification,
          emailResent: err.emailResent,
          message: err.message
        };
        setError(err.error || 'A verification email is sent, please verify before login');
        throw emailVerificationError;
      }
      
      setError(err.error || 'Login failed');
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, [resetInactivityTimer]);

  // Register function - updated for email verification
  const register = useCallback(async (username, email, password, invitationCode) => {
    try {
      setAuthLoading(true);
      setError('');

      const response = await authApi.register({
        username,
        email,
        password,
        invitation_code: invitationCode
      });

      // Registration successful but requires email verification
      if (response.data.email_verification_required) {
        return {
          success: true,
          emailVerificationRequired: true,
          message: response.data.message,
          email: response.data.user?.email || email,
          emailSent: response.data.email_sent,
          warning: response.data.warning
        };
      }

      // Fallback for legacy response (in case backend doesn't have email verification yet)
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        setCurrentUser(response.data.user);
        
        // Start the inactivity timer
        resetInactivityTimer();
        return response.data;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, [resetInactivityTimer]);

  // Email verification function
  const verifyEmail = useCallback(async (token, email) => {
    try {
      setAuthLoading(true);
      setError('');
      
      const response = await authApi.verifyEmail(token, email);
      
      return {
        success: true,
        message: response.data.message,
        emailVerified: response.data.email_verified
      };
    } catch (err) {
      const errorData = err.response?.data || {};
      setError(errorData.error || 'Email verification failed');
      
      return {
        success: false,
        error: errorData.error,
        expired: errorData.expired,
        message: errorData.error || 'Email verification failed'
      };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Resend verification email function
  const resendVerificationEmail = useCallback(async (email) => {
    try {
      setAuthLoading(true);
      setError('');
      
      const response = await authApi.resendVerification(email);
      
      return {
        success: true,
        message: response.data.message,
        emailSent: response.data.email_sent
      };
    } catch (err) {
      const errorData = err.response?.data || {};
      setError(errorData.error || 'Failed to resend verification email');
      
      return {
        success: false,
        error: errorData.error,
        rateLimited: errorData.rate_limited,
        message: errorData.message || errorData.error || 'Failed to resend verification email'
      };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Get email verification status
  const getEmailVerificationStatus = useCallback(async () => {
    try {
      const response = await authApi.getEmailVerificationStatus();
      return response.data;
    } catch (err) {
      return null;
    }
  }, []);
  
  // Initial setup effect - runs only once
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Check authentication
      checkAuthStatus();
      
      // Load saved inactivity timeout if available
      const savedTimeout = localStorage.getItem('inactivityTimeout');
      if (savedTimeout) {
        const parsedTimeout = parseInt(savedTimeout, 10);
        if (!isNaN(parsedTimeout)) {
          setInactivityTimeout(parsedTimeout);
        }
      }
    } else {
      setLoading(false);
    }
  }, []); // Empty dependency array for initial setup only
  
  // Effect for setting up activity listeners - runs when handleUserActivity changes
  useEffect(() => {
    // Only set up listeners if there's a user
    if (!currentUser) return;

    // Set up event listeners for user activity - expanded with more significant interactions
    const basicActivityEvents = ['mousemove', 'keypress', 'scroll', 'touchstart'];
    const significantActivityEvents = ['mousedown', 'keydown', 'click', 'input', 'change', 'submit'];
    
    // Function to handle significant activities (with higher weight)
    const handleSignificantActivity = (e) => {
      // For significant activities, we'll count them with more weight
      // This helps differentiate passive presence from active engagement
      const now = Date.now();
      activityTimestampsRef.current.push(now);
      activityTimestampsRef.current.push(now); // Add twice to give more weight
      
      // Keep only the most recent activities
      while (activityTimestampsRef.current.length > MAX_ACTIVITY_HISTORY) {
        activityTimestampsRef.current.shift();
      }
      
      // Then call the regular activity handler
      handleUserActivity(e);
    };
    
    // Add event listeners
    basicActivityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });
    
    significantActivityEvents.forEach(event => {
      window.addEventListener(event, handleSignificantActivity);
    });
    
    // Also track API requests as significant user activity
    const originalRequest = axios.request;
    axios.request = function(...args) {
      // Count API requests initiated by the UI as significant activity
      // Exclude background/polling API calls that might happen regularly
      const url = args[0]?.url || '';
      if (!url.includes('/api/auth/check-auth') && !url.includes('/api/health')) {
        handleSignificantActivity({ type: 'api-request' });
      }
      return originalRequest.apply(this, args);
    };
    
    // Start timer on mount
    resetInactivityTimer();
    
    // Cleanup function
    return () => {
      // Remove event listeners
      basicActivityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      
      significantActivityEvents.forEach(event => {
        window.removeEventListener(event, handleSignificantActivity);
      });
      
      // Restore original axios request
      axios.request = originalRequest;
      
      // Clear the timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [currentUser, handleUserActivity, resetInactivityTimer]);

  // Effect to handle timeout changes
  useEffect(() => {
    if (currentUser) {
      resetInactivityTimer();
    }
  }, [currentUser, inactivityTimeout, resetInactivityTimer]);

  // Add this function to the AuthProvider component
  const changePassword = useCallback(async (currentPassword, newPassword, twoFactorCode = null) => {
    try {
      setAuthLoading(true);
      setError('');
      
      // Check if token exists
      const token = localStorage.getItem('token');
      
      const body = {
        current_password: currentPassword,
        new_password: newPassword
      };
      if (twoFactorCode) {
        body.two_fa_code = twoFactorCode;
      }
      const response = await apiClient.post('/auth/change-password', body);

      if (response.data.success) {
        return response.data;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Password change failed');
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Google OAuth login function
  const googleLogin = useCallback(async (googleToken, invitationCode) => {
    try {
      // Don't set loading state here - let the UI component handle it entirely
      setError('');

      console.log('🔍 AuthContext googleLogin - making API call');

      const requestData = {
        token: googleToken
      };

      // Only include invitation_code if provided (for new users)
      if (invitationCode) {
        requestData.invitation_code = invitationCode;
      }

      // Add trusted device token if available
      const trustedDeviceToken = localStorage.getItem('trustedDeviceToken');
      if (trustedDeviceToken) {
        requestData.device_token = trustedDeviceToken;
        console.log('🔍 AuthContext googleLogin - including trusted device token');
      }

      const response = await axios.post('/api/auth/google-login', requestData);
      console.log('🔍 AuthContext googleLogin - API response:', response.data);

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        console.log('🔍 AuthContext googleLogin - 2FA required, returning response data');

        // If we sent a device token but backend still requires 2FA,
        // the token is likely expired - clean it up
        if (trustedDeviceToken) {
          console.log('🔍 AuthContext googleLogin - removing expired trusted device token');
          localStorage.removeItem('trustedDeviceToken');
        }

        // Return the 2FA requirement response - let the UI handle everything
        return response.data;
      }

      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        setCurrentUser(response.data.user);

        // Start the inactivity timer
        resetInactivityTimer();
        return response.data;
      }

      // If no access_token but also no 2FA requirement, something went wrong
      throw new Error('Login failed - no access token received');
    } catch (err) {
      console.log('🔍 AuthContext googleLogin ERROR:', err);
      setError(err.response?.data?.error || 'Google login failed');
      throw err;
    }
    // No finally block - let the UI component handle all loading state
  }, [resetInactivityTimer]);

  // Google OAuth 2FA completion function
  const googleLogin2FA = useCallback(async (userId, twoFactorCode, trustDevice = false, deviceName = null) => {
    try {
      setAuthLoading(true);
      setError('');

      const requestData = {
        user_id: userId,
        two_fa_code: twoFactorCode
      };

      if (trustDevice && deviceName) {
        requestData.trust_device = true;
        requestData.device_name = deviceName;
      }

      const response = await axios.post('/api/auth/google-login-2fa', requestData);

      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);

        // Store trusted device token if provided
        if (response.data.device_token) {
          localStorage.setItem('trustedDeviceToken', response.data.device_token);
        }

        setCurrentUser(response.data.user);

        // Start the inactivity timer
        resetInactivityTimer();
        return response.data;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Google 2FA login failed');
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, [resetInactivityTimer]);

  // Export context values. Memoized so a new object identity is only handed
  // to the Provider when something in it actually changed - without this,
  // every AuthProvider render (e.g. the activityCount/activityIntensity
  // state updates handleUserActivity triggers on every throttled user
  // interaction, at most every 3s during active use) produced a brand new
  // `value` object, which re-rendered all ~26 components across the app
  // that call useAuth(), regardless of whether the piece of context they
  // actually read had changed. All the functions above are now wrapped in
  // useCallback so their identity stays stable across renders that don't
  // change their own dependencies, which lets this memoization actually
  // hold instead of recomputing every render anyway.
  const value = useMemo(() => ({
    currentUser,
    loading,
    authLoading,
    isAuthenticated: !!currentUser,
    login,
    logout,
    register,
    googleLogin,
    googleLogin2FA,
    checkAuthStatus,
    inactivityTimeout,
    updateInactivityTimeout,
    resetInactivityTimer,
    error,
    changePassword,
    showWarning,
    setShowWarning,
    activityIntensity, // Expose activity level for potential UI feedback
    // Email verification functions
    verifyEmail,
    resendVerificationEmail,
    getEmailVerificationStatus,
  }), [
    currentUser,
    loading,
    authLoading,
    login,
    logout,
    register,
    googleLogin,
    googleLogin2FA,
    checkAuthStatus,
    inactivityTimeout,
    updateInactivityTimeout,
    resetInactivityTimer,
    error,
    changePassword,
    showWarning,
    activityIntensity,
    verifyEmail,
    resendVerificationEmail,
    getEmailVerificationStatus,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showWarning && <InactivityWarning />}
    </AuthContext.Provider>
  );
} 